import { useState, useEffect, createContext, useContext } from "react";
import { Outlet, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Heart, 
  ShoppingBag, 
  ShoppingCart,
  MessageCircle, 
  User,
  LogOut,
  Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Customer Auth Context
// [CONV-1-SYSTEME] Export context for direct use
export const CustomerAuthContext = createContext(null);

export function useCustomerAuth() {
  return useContext(CustomerAuthContext);
}

export default function CustomerLayout() {
  const { userId } = useParams(); // Store ID from URL
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [boutiqueName, setBoutiqueName] = useState("La boutique");
  const [devise, setDevise] = useState("€");
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);

  useEffect(() => {
    checkAuth();
  }, [userId]);

  const checkAuth = async () => {
    const token = localStorage.getItem(`customer_token_${userId}`);
    if (!token) {
      setLoading(false);
      navigate(`/vitrine/${userId}/login`);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/customer/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data.customer);
      setBoutiqueName(response.data.boutique_name || response.data.store_name || "La boutique");
      setUnreadMessages(response.data.unread_messages || 0);
      if (response.data.devise) setDevise(response.data.devise);
      
      // Get favorites count
      const favRes = await axios.get(`${API}/api/customer/favorites/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavoritesCount(favRes.data.count);
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem(`customer_token_${userId}`);
      navigate(`/vitrine/${userId}/login`);
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem(`customer_token_${userId}`);
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const logout = () => {
    localStorage.removeItem(`customer_token_${userId}`);
    setCustomer(null);
    navigate(`/vitrine/${userId}`);
    toast.success("Déconnexion réussie");
  };

  const refreshCounts = async () => {
    try {
      const token = localStorage.getItem(`customer_token_${userId}`);
      if (!token) return;
      
      const [msgRes, favRes] = await Promise.all([
        axios.get(`${API}/api/customer/messages/unread`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/customer/favorites/count`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setUnreadMessages(msgRes.data.unread_count || 0);
      setFavoritesCount(favRes.data.count || 0);
    } catch (error) {
      console.error("Failed to refresh counts:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const navItems = [
    { path: "favorites", icon: Heart, label: "Favoris", badge: favoritesCount },
    // { path: "cart", icon: ShoppingCart, label: "Panier" }, // Masqué temporairement
    { path: "orders", icon: ShoppingBag, label: "Commandes" },
    { path: "messages", icon: MessageCircle, label: "Messages", badge: unreadMessages },
    { path: "profile", icon: User, label: "Profil" },
  ];

  return (
    <CustomerAuthContext.Provider value={{ 
      customer, 
      storeName: boutiqueName,
      boutiqueName,
      devise,
      userId,
      getAuthHeaders, 
      logout, 
      refreshCounts,
      unreadMessages,
      favoritesCount
    }}>
      <div className="min-h-screen bg-[#FAFAF7] pb-20">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-[#F0F0F0]">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-sm text-[#888888]">{boutiqueName}</p>
              <h1 className="text-lg font-semibold text-[#1B1B1B]">
                Bonjour {customer?.first_name} 👋
              </h1>
            </div>
            <NavLink 
              to={`/vitrine/${userId}`}
              className="text-sm text-[#D4AF37] hover:underline"
            >
              Voir la boutique
            </NavLink>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-lg mx-auto">
          <Outlet />
        </main>

        {/* Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#F0F0F0] z-40">
          <div className="max-w-lg mx-auto flex items-center justify-around py-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={`/vitrine/${userId}/espace/${item.path}`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                    isActive 
                      ? 'text-[#D4AF37]' 
                      : 'text-[#888888] hover:text-[#1B1B1B]'
                  }`
                }
              >
                <div className="relative">
                  <item.icon className="w-6 h-6" />
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span className="text-xs">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </CustomerAuthContext.Provider>
  );
}
